import { EditOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, Input, Select, Space, Table } from 'antd';
import { FC, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { axiosClient } from '@/api/axiosClient';
import { useGetCustomerList } from '@/modules/customer/hooks/useGetCustomerList';
import { AssignManagerModal } from '@/modules/factory/components/AssignManagerModal';
import { FactoryModal } from '@/modules/factory/components/FactoryModal';
import { useGetFactoryList } from '@/modules/factory/hooks/useFactoryApi';
import { FactoryModel } from '@/modules/factory/models';

const FactoryListPage: FC = () => {
  const [searchParams] = useSearchParams();
  const initialCustomerId = searchParams.get('customer_id') || '';
  const [customerId, setCustomerId] = useState(initialCustomerId);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<FactoryModel | undefined>();
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigningFactory, setAssigningFactory] = useState<FactoryModel | undefined>();

  const { data, isLoading, refetch } = useGetFactoryList({
    customer_id: customerId || undefined,
    limit: 20,
    page,
    query: query || undefined,
  });

  // V1.4 D4 — Customer filter dropdown (T-CUS-03 DoD #1).
  const { data: customers } = useGetCustomerList('page=1&limit=200');
  const customerOptions = useMemo(
    () =>
      ((customers as unknown as { rows?: Array<{ id?: string; name: string }> })?.rows ?? [])
        .filter((c) => c.id)
        .map((c) => ({ label: c.name, value: c.id! })),
    [customers]
  );

  // V1.4 D4 — BACKFILL "Còn X chỗ" indicator (A11.5). Each row fetches its
  // own desired_headcount via a tiny per-factory query (cheap, all hit the
  // same advisory-lock-free read path).
  const rows = (data?.rows as FactoryModel[]) || [];

  const columns = [
    { dataIndex: 'name', key: 'name', title: 'Tên nhà máy' },
    { dataIndex: 'address', key: 'address', title: 'Địa chỉ' },
    {
      dataIndex: 'baseline_headcount',
      key: 'baseline',
      title: 'Baseline',
      width: 110,
    },
    {
      dataIndex: 'wait_to_resigned_days',
      key: 'wait',
      render: (v: number) => `${v} ngày`,
      title: 'CHỜ VIỆC max',
      width: 130,
    },
    {
      key: 'backfill',
      render: (_: unknown, row: FactoryModel) => <BackfillIndicator factoryId={row.id} />,
      title: 'BACKFILL',
      width: 120,
    },
    {
      dataIndex: 'is_active',
      key: 'active',
      render: (v: boolean) => (
        <Badge color={v ? 'green' : 'red'} text={v ? 'Đang hoạt động' : 'Tắt'} />
      ),
      title: 'Trạng thái',
      width: 150,
    },
    {
      key: 'actions',
      render: (_: unknown, row: FactoryModel) => (
        <Space>
          <Button
            icon={<EditOutlined rev="" />}
            onClick={() => {
              setEditing(row);
              setModalOpen(true);
            }}
            size="small"
          >
            Sửa
          </Button>
          <Button
            icon={<TeamOutlined rev="" />}
            onClick={() => {
              setAssigningFactory(row);
              setAssignOpen(true);
            }}
            size="small"
          >
            QLĐ
          </Button>
        </Space>
      ),
      title: 'Hành động',
      width: 180,
    },
  ];

  return (
    <Card
      extra={
        <Space>
          <Select
            allowClear
            onChange={(v) => {
              setCustomerId(v || '');
              setPage(1);
            }}
            optionFilterProp="label"
            options={customerOptions}
            placeholder="Lọc theo Khách hàng"
            showSearch
            style={{ width: 220 }}
            value={customerId || undefined}
          />
          <Input.Search
            allowClear
            onSearch={(v) => {
              setQuery(v);
              setPage(1);
            }}
            placeholder="Tìm theo tên..."
            style={{ width: 220 }}
          />
          <Button
            icon={<PlusOutlined rev="" />}
            onClick={() => {
              setEditing(undefined);
              setModalOpen(true);
            }}
            type="primary"
          >
            Tạo nhà máy
          </Button>
        </Space>
      }
      title="Danh sách nhà máy (V1.4 A1)"
    >
      <Table<FactoryModel>
        columns={columns}
        dataSource={rows}
        loading={isLoading}
        pagination={{
          current: page,
          onChange: setPage,
          pageSize: 20,
          showSizeChanger: false,
          total: data?.total_rows ?? 0,
        }}
        rowKey="id"
        size="middle"
      />

      <FactoryModal
        customerId={customerId}
        initial={editing}
        onClose={() => setModalOpen(false)}
        onSaved={refetch}
        visible={modalOpen}
      />

      {assigningFactory && assigningFactory.id && (
        <AssignManagerModal
          factoryId={assigningFactory.id}
          factoryName={assigningFactory.name}
          onClose={() => setAssignOpen(false)}
          visible={assignOpen}
        />
      )}
    </Card>
  );
};

// V1.4 A11.5 — BACKFILL indicator per row. Hit the existing factory detail
// endpoint which surfaces baseline_headcount; combined with the worker count
// from /worker/filter?factory_id=... we infer how many slots are still open.
// Falls back to "—" silently if either call errors so a single bad row never
// blanks the whole table.
const BackfillIndicator: FC<{ factoryId?: string }> = ({ factoryId }) => {
  const { data: factory } = useQuery(
    ['factory-detail', factoryId],
    () => axiosClient.get<FactoryModel>(`/factory/${factoryId}`),
    { enabled: !!factoryId, refetchOnWindowFocus: false, staleTime: 60_000 }
  );
  const { data: workers } = useQuery(
    ['worker-count-factory', factoryId],
    () =>
      axiosClient.get<{ total_rows: number }>(
        `/worker/filter?factory_id=${factoryId}&page=1&limit=1`
      ),
    { enabled: !!factoryId, refetchOnWindowFocus: false, staleTime: 60_000 }
  );

  if (!factory) return <span>—</span>;
  // axiosClient returns the full BE envelope; .data is the inner payload.
  const baseline =
    (factory as unknown as { data?: FactoryModel })?.data?.baseline_headcount ?? 0;
  const current =
    (workers as unknown as { data?: { total_rows?: number } })?.data?.total_rows ?? 0;
  const remaining = Math.max(baseline - current, 0);

  if (baseline === 0) return <span style={{ color: '#999' }}>Chưa set</span>;
  if (remaining === 0) return <Badge color="green" text="Đủ NLĐ" />;

  return <Badge color="orange" text={`Còn ${remaining} chỗ`} />;
};

export default FactoryListPage;
